import { useState, useCallback } from "react";
import axios from "../Axios";
import baseAxios from "axios";
import { Urls } from "../Urls";
import { API_STORAGE } from "../Components/Constants";

const AZURE_CONTAINER_NAME = "private-container";

export function useAzureUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Reserves a signed URL from backend for file upload.
   */
  const reserveAzureSignedUrl = useCallback(async (filename) => {
    const reservePayload = {
      action: "upload",
      fileName: filename,
      accountName: API_STORAGE,
      containerName: AZURE_CONTAINER_NAME,
    };

    const { data } = await axios.post(Urls.uploadServiceNew, reservePayload);
    const signedUrl = data?.url;
    const fileName = data?.file_name;

    if (!signedUrl || typeof signedUrl !== "string") {
      throw new Error("Invalid upload URL received from backend.");
    }

    return { signedUrl, fileName };
  }, []);

  /**
   * Uploads file to Azure Blob using signed URL.
   */
  const putFileToAzureBlob = useCallback(async (file, signedUrl) => {
    await baseAxios.put(signedUrl, file, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "x-ms-blob-content-disposition": `attachment; filename="${file.name}"`,
      },
    });

    return signedUrl.split("?")[0]; // strip query params → blob URL
  }, []);

  /**
   * High-level upload: reserves URL + uploads file.
   */
  const uploadFile = useCallback(
    async (file, filename) => {
      setIsUploading(true);
      setError(null);

      try {
        const { signedUrl, fileName } = await reserveAzureSignedUrl(filename);
        const blobUrl = await putFileToAzureBlob(file, signedUrl);
        return { blobUrl, fileName };
      } catch (err) {
        setError(err.message || "Upload failed");
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [reserveAzureSignedUrl, putFileToAzureBlob]
  );

  return { uploadFile, isUploading, error };
}
